import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, RefreshCw, Clock, CheckCircle2, AlertTriangle,
  TrendingUp, ChevronDown, ChevronUp, Info, Activity,
  Database,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface KpiAutoResult {
  code: string;
  tcCode: string;
  tcName: string;
  kpiName: string;
  unit: string;
  rawValue: number | null;
  score: number | null;
  converterFn: string;
  collectedAt: string | null;
  status: 'OK' | 'PENDING' | 'ERROR' | 'NA';
  errorMsg: string | null;
  dataSource: string | null;
  weight: number;
}

interface CollectionRun {
  jobId: string;
  triggeredAt: string;
  triggeredBy: 'CRON' | 'MANUAL';
  status: 'SUCCESS' | 'FAILED' | 'RUNNING';
  kpiCount: number;
  durationMs: number | null;
  errorSummary: string | null;
}

interface CronInfo {
  schedule: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastJobId: string | null;
  bullQueueActive: boolean;
  queueDepth: number;
}

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_KPIS: KpiAutoResult[] = [
  {
    code: 'TC1a', tcCode: 'TC1', tcName: 'Pháp lý & Công bố', weight: 30,
    kpiName: 'Tỷ lệ CSDL đã công bố / toàn bộ CSDL (%)',
    unit: '%', rawValue: 78, score: 7, converterFn: 'percentageToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'data_assets table',
  },
  {
    code: 'TC4a', tcCode: 'TC4', tcName: 'Bảo mật & An toàn dữ liệu', weight: 30,
    kpiName: 'Tỷ lệ tài sản dữ liệu có biện pháp bảo vệ (%)',
    unit: '%', rawValue: 85, score: 8, converterFn: 'percentageToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'data_assets + security_config',
  },
  {
    code: 'TC4c', tcCode: 'TC4', tcName: 'Bảo mật & An toàn dữ liệu', weight: 20,
    kpiName: 'Số lỗ hổng CVSS ≥ 7.0 chưa xử lý quá 30 ngày',
    unit: 'cve', rawValue: 2, score: 7, converterFn: 'cvssCountToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'security_events (CVSS)',
  },
  {
    code: 'TC5a', tcCode: 'TC5', tcName: 'Giám sát & Phản ứng sự cố', weight: 30,
    kpiName: 'Tỷ lệ log thu thập được / log yêu cầu (%)',
    unit: '%', rawValue: 92, score: 9, converterFn: 'percentageToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'soc_connection + log_metrics',
  },
  {
    code: 'TC5b', tcCode: 'TC5', tcName: 'Giám sát & Phản ứng sự cố', weight: 30,
    kpiName: 'MTTR trung bình sự cố bảo mật (giờ)',
    unit: 'h', rawValue: 18, score: 7, converterFn: 'mttrToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'security_events (resolved)',
  },
  {
    code: 'TC6a', tcCode: 'TC6', tcName: 'Tính sẵn sàng & Phục hồi', weight: 50,
    kpiName: 'Uptime CSDL quan trọng trong 30 ngày qua (%)',
    unit: '%', rawValue: 99.2, score: 10, converterFn: 'percentageToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'monitoring_metrics',
  },
  {
    code: 'TC6b', tcCode: 'TC6', tcName: 'Tính sẵn sàng & Phục hồi', weight: 50,
    kpiName: 'Độ trễ phục hồi dữ liệu RTO (phút)',
    unit: 'min', rawValue: 45, score: 7, converterFn: 'mttrToScore',
    collectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
    status: 'OK', errorMsg: null, dataSource: 'backup_restore_logs',
  },
];

const MOCK_CRON: CronInfo = {
  schedule: '0 2 * * *',
  nextRunAt: new Date(new Date().setHours(2, 0, 0, 0) + 86400_000).toISOString(),
  lastRunAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
  lastJobId: 'job-kpi-2026-0608-020034',
  bullQueueActive: true,
  queueDepth: 0,
};

const MOCK_HISTORY: CollectionRun[] = [
  { jobId: 'job-kpi-2026-0608', triggeredAt: new Date(Date.now() - 3 * 3600_000).toISOString(), triggeredBy: 'CRON', status: 'SUCCESS', kpiCount: 7, durationMs: 4821, errorSummary: null },
  { jobId: 'job-kpi-2026-0607', triggeredAt: new Date(Date.now() - 27 * 3600_000).toISOString(), triggeredBy: 'CRON', status: 'SUCCESS', kpiCount: 7, durationMs: 5103, errorSummary: null },
  { jobId: 'job-kpi-2026-0606', triggeredAt: new Date(Date.now() - 51 * 3600_000).toISOString(), triggeredBy: 'CRON', status: 'FAILED', kpiCount: 4, durationMs: 2100, errorSummary: 'TC5b: MTTR query timeout' },
  { jobId: 'job-kpi-2026-0605m', triggeredAt: new Date(Date.now() - 74 * 3600_000).toISOString(), triggeredBy: 'MANUAL', status: 'SUCCESS', kpiCount: 7, durationMs: 6200, errorSummary: null },
];

// ─── Converter function docs ──────────────────────────────────────────────────
const CONVERTER_DOCS: Record<string, { desc: string; formula: string }> = {
  percentageToScore: {
    desc: 'Chuyển đổi tỷ lệ % sang điểm 0-10',
    formula: '0→0, <50→3, <70→5, <85→7, <95→9, ≥95→10',
  },
  mttrToScore: {
    desc: 'Chuyển MTTR/RTO (giờ/phút) sang điểm 0-10',
    formula: '>72h→0, ≤72h→3, ≤48h→5, ≤24h→7, ≤8h→9, ≤4h→10',
  },
  cvssCountToScore: {
    desc: 'Chuyển số lỗ hổng CVSS≥7.0 chưa xử lý >30 ngày sang điểm',
    formula: '>5→0, >3→3, >1→5, =1→7, =0→10 (HC1: >0 quá 30 ngày→C)',
  },
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ kpi }: { kpi: KpiAutoResult }) {
  const [expanded, setExpanded] = useState(false);
  const conv = CONVERTER_DOCS[kpi.converterFn];
  const scoreColor = !kpi.score ? 'text-slate-400' :
    kpi.score >= 8 ? 'text-emerald-500' :
    kpi.score >= 5 ? 'text-amber-500' : 'text-rose-500';

  return (
    <motion.div
      layout
      className={`bg-white dark:bg-white/[0.02] border rounded-2xl overflow-hidden transition-colors ${
        kpi.status === 'ERROR' ? 'border-rose-200 dark:border-rose-500/30' :
        kpi.status === 'OK'    ? 'border-slate-200 dark:border-white/5' :
        'border-amber-200 dark:border-amber-500/20'
      }`}
    >
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-all"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-start gap-4">
          {/* Code badge */}
          <div className="shrink-0 text-center">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-500/15 flex flex-col items-center justify-center">
              <span className="text-[9px] font-black text-indigo-500 uppercase">{kpi.tcCode}</span>
              <span className="text-[11px] font-black text-indigo-700 dark:text-indigo-300">{kpi.code}</span>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-slate-900 dark:text-white leading-snug">{kpi.kpiName}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-[10px] text-slate-400 font-medium">{kpi.tcName}</span>
              {kpi.status === 'OK' && (
                <span className="text-[10px] text-emerald-500 font-black flex items-center gap-1">
                  <CheckCircle2 size={10} /> OK
                </span>
              )}
              {kpi.status === 'ERROR' && (
                <span className="text-[10px] text-rose-500 font-black flex items-center gap-1">
                  <AlertTriangle size={10} /> ERROR
                </span>
              )}
              {kpi.status === 'PENDING' && (
                <span className="text-[10px] text-amber-500 font-black">⏳ Chờ</span>
              )}
            </div>
            {kpi.collectedAt && (
              <p className="text-[10px] text-slate-400 mt-1">
                {new Date(kpi.collectedAt).toLocaleString('vi-VN')}
                {kpi.dataSource && <span className="ml-2 font-mono">← {kpi.dataSource}</span>}
              </p>
            )}
          </div>

          <div className="shrink-0 text-right">
            <div className={`text-2xl font-black tabular-nums ${scoreColor}`}>
              {kpi.score ?? '—'}
              <span className="text-xs text-slate-400 font-medium">/10</span>
            </div>
            <div className="text-xs text-slate-500 font-bold mt-0.5">
              {kpi.rawValue !== null ? `${kpi.rawValue.toLocaleString('vi-VN')} ${kpi.unit}` : '—'}
            </div>
            {expanded
              ? <ChevronUp size={14} className="text-slate-400 mt-1 ml-auto" />
              : <ChevronDown size={14} className="text-slate-400 mt-1 ml-auto" />
            }
          </div>
        </div>

        {/* Score bar */}
        {kpi.score !== null && (
          <div className="mt-3 h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${kpi.score * 10}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className={`h-full rounded-full ${
                kpi.score >= 8 ? 'bg-emerald-500' :
                kpi.score >= 5 ? 'bg-amber-500' : 'bg-rose-500'
              }`}
            />
          </div>
        )}
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 space-y-3 border-t border-slate-100 dark:border-white/5">
              {kpi.errorMsg && (
                <div className="mt-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20">
                  <p className="text-xs text-rose-600 dark:text-rose-400 font-bold">{kpi.errorMsg}</p>
                </div>
              )}

              {conv && (
                <div className="mt-3 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20">
                  <p className="text-[10px] font-black text-indigo-500 uppercase tracking-wider mb-1">
                    Hàm chuyển đổi: <code className="bg-indigo-100 dark:bg-indigo-500/20 px-1 rounded">{kpi.converterFn}()</code>
                  </p>
                  <p className="text-[11px] text-indigo-600 dark:text-indigo-400">{conv.desc}</p>
                  <p className="text-[10px] font-mono text-indigo-500 mt-1">{conv.formula}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 mt-2">
                <div className="bg-slate-50 dark:bg-white/[0.02] rounded-xl p-2.5 text-center">
                  <p className="text-[9px] text-slate-400 font-black uppercase">Trọng số KPI</p>
                  <p className="text-sm font-black text-slate-700 dark:text-white">{kpi.weight}%</p>
                </div>
                <div className="bg-slate-50 dark:bg-white/[0.02] rounded-xl p-2.5 text-center">
                  <p className="text-[9px] text-slate-400 font-black uppercase">Giá trị thô</p>
                  <p className="text-sm font-black text-slate-700 dark:text-white">{kpi.rawValue ?? '—'} {kpi.unit}</p>
                </div>
                <div className="bg-slate-50 dark:bg-white/[0.02] rounded-xl p-2.5 text-center">
                  <p className="text-[9px] text-slate-400 font-black uppercase">Điểm quy đổi</p>
                  <p className={`text-sm font-black ${scoreColor}`}>{kpi.score ?? '—'} / 10</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function KpiCollector() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'kpis' | 'history' | 'cron'>('kpis');
  const [isTriggering, setIsTriggering] = useState(false);

  const { data: kpis = MOCK_KPIS, isLoading: loadingKpis } = useQuery<KpiAutoResult[]>({
    queryKey: ['kpi-collector-auto'],
    queryFn: () => api.get('/collector/kpi-auto/latest').then(r => r.data ?? MOCK_KPIS).catch(() => MOCK_KPIS),
    refetchInterval: 60_000,
  });

  const { data: cron = MOCK_CRON } = useQuery<CronInfo>({
    queryKey: ['kpi-cron-info'],
    queryFn: () => api.get('/collector/cron/status').then(r => r.data ?? MOCK_CRON).catch(() => MOCK_CRON),
    refetchInterval: 30_000,
  });

  const { data: history = MOCK_HISTORY } = useQuery<CollectionRun[]>({
    queryKey: ['kpi-collection-history'],
    queryFn: () => api.get('/collector/kpi-auto/history').then(r => r.data ?? MOCK_HISTORY).catch(() => MOCK_HISTORY),
    enabled: activeTab === 'history',
  });

  const trigger = useMutation({
    mutationFn: () => api.post('/collector/kpi-auto/trigger'),
    onMutate: () => setIsTriggering(true),
    onSuccess: () => {
      setIsTriggering(false);
      qc.invalidateQueries({ queryKey: ['kpi-collector-auto'] });
      qc.invalidateQueries({ queryKey: ['kpi-collection-history'] });
      toast.success('Đã kích hoạt thu thập 7 KPI AUTO');
    },
    onError: () => { setIsTriggering(false); toast.error('Lỗi kích hoạt KPI collector'); },
  });

  const okCount = kpis.filter(k => k.status === 'OK').length;
  const avgScore = kpis.length ? (kpis.reduce((s, k) => s + (k.score ?? 0), 0) / kpis.length).toFixed(1) : '—';

  const timeDiff = (iso: string | null) => {
    if (!iso) return '—';
    const diff = new Date(iso).getTime() - Date.now();
    const abs = Math.abs(diff);
    if (abs < 60_000) return diff < 0 ? 'vừa xong' : 'ngay bây giờ';
    if (abs < 3600_000) return `${Math.round(abs / 60_000)} phút ${diff < 0 ? 'trước' : 'nữa'}`;
    return `${Math.round(abs / 3600_000)} giờ ${diff < 0 ? 'trước' : 'nữa'}`;
  };

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
            KPI <span className="text-indigo-500">Collector</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            7 KPI tự động (TTDLQG_AUTO) · Cron 02:00 hàng ngày · Bull Queue
          </p>
        </div>
        <button
          onClick={() => trigger.mutate()}
          disabled={isTriggering}
          className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-indigo-600 text-white font-black text-sm hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-lg shadow-indigo-500/20"
        >
          {isTriggering
            ? <><RefreshCw size={16} className="animate-spin" /> Đang thu thập...</>
            : <><Play size={16} /> Chạy ngay</>
          }
        </button>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'KPI thu thập OK', value: `${okCount} / 7`, color: okCount === 7 ? 'emerald' : 'amber', icon: CheckCircle2 },
          { label: 'Điểm TB AUTO', value: avgScore !== '—' ? `${avgScore}/10` : '—', color: 'indigo', icon: TrendingUp },
          { label: 'Lần chạy cuối', value: timeDiff(cron.lastRunAt), color: 'slate', icon: Clock },
          { label: 'Chạy tiếp theo', value: timeDiff(cron.nextRunAt), color: 'blue', icon: Activity },
        ].map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-5"
          >
            <div className={`w-9 h-9 rounded-xl bg-${c.color}-100 dark:bg-${c.color}-500/15 flex items-center justify-center mb-3`}>
              <c.icon size={16} className={`text-${c.color}-600 dark:text-${c.color}-400`} />
            </div>
            <p className="text-xl font-black text-slate-900 dark:text-white">{c.value}</p>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5 uppercase tracking-wider">{c.label}</p>
          </motion.div>
        ))}
      </div>

      {/* ── Cron status bar ── */}
      <div className={`flex items-center gap-4 p-4 rounded-2xl border ${
        cron.bullQueueActive
          ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20'
          : 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20'
      }`}>
        <div className={`w-2 h-2 rounded-full ${cron.bullQueueActive ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
        <div className="flex-1">
          <p className={`text-xs font-black ${cron.bullQueueActive ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
            Bull Queue: {cron.bullQueueActive ? 'ACTIVE' : 'PAUSED'}
            {cron.queueDepth > 0 && ` · ${cron.queueDepth} job đang chờ`}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Schedule: <code className="font-mono">{cron.schedule}</code> (02:00 AM hàng ngày)
            · Job ID cuối: <code className="font-mono">{cron.lastJobId ?? '—'}</code>
          </p>
        </div>
        <div className="text-right text-xs text-slate-500">
          <p>Cron tiếp theo: <strong>{timeDiff(cron.nextRunAt)}</strong></p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-2xl w-fit">
        {(['kpis', 'history', 'cron'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
              activeTab === tab
                ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab === 'kpis' ? '7 KPI AUTO' : tab === 'history' ? 'Lịch sử chạy' : 'Cấu hình Cron'}
          </button>
        ))}
      </div>

      {/* ── KPIs Tab ── */}
      {activeTab === 'kpis' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl border border-indigo-200 dark:border-indigo-500/20">
            <Info size={14} className="text-indigo-500 shrink-0" />
            <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
              7 KPI này được thu thập tự động từ CSDL nội bộ và Data Assets. Nhấn vào từng KPI để xem hàm chuyển đổi và công thức tính điểm.
            </p>
          </div>

          {loadingKpis ? (
            <div className="text-center py-16 text-slate-400">Đang tải...</div>
          ) : (
            <div className="space-y-2">
              {kpis.map(kpi => <KpiCard key={kpi.code} kpi={kpi} />)}
            </div>
          )}
        </div>
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {history.map((run, i) => (
            <motion.div
              key={run.jobId}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`flex items-center gap-4 p-4 rounded-2xl border ${
                run.status === 'SUCCESS' ? 'bg-white dark:bg-white/[0.02] border-slate-200 dark:border-white/5' :
                run.status === 'FAILED'  ? 'bg-rose-50 dark:bg-rose-500/5 border-rose-200 dark:border-rose-500/20' :
                'bg-amber-50 dark:bg-amber-500/5 border-amber-200 dark:border-amber-500/20'
              }`}
            >
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                run.status === 'SUCCESS' ? 'bg-emerald-100 dark:bg-emerald-500/15' :
                run.status === 'FAILED'  ? 'bg-rose-100 dark:bg-rose-500/15' : 'bg-amber-100 dark:bg-amber-500/15'
              }`}>
                {run.status === 'SUCCESS' ? <CheckCircle2 size={16} className="text-emerald-500" /> :
                 run.status === 'FAILED'  ? <AlertTriangle size={16} className="text-rose-500" /> :
                 <RefreshCw size={16} className="text-amber-500 animate-spin" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-slate-900 dark:text-white font-mono">{run.jobId}</span>
                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                    run.triggeredBy === 'CRON'
                      ? 'bg-slate-100 dark:bg-white/5 text-slate-500'
                      : 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400'
                  }`}>
                    {run.triggeredBy}
                  </span>
                </div>
                {run.errorSummary && (
                  <p className="text-[10px] text-rose-500 mt-0.5">{run.errorSummary}</p>
                )}
              </div>

              <div className="text-right shrink-0 text-xs text-slate-500">
                <p className="font-bold">{run.kpiCount}/7 KPI</p>
                <p>{run.durationMs ? `${(run.durationMs / 1000).toFixed(1)}s` : '—'}</p>
                <p className="text-[10px] mt-0.5">{new Date(run.triggeredAt).toLocaleString('vi-VN')}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── Cron Config Tab ── */}
      {activeTab === 'cron' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
            <h3 className="font-black text-slate-900 dark:text-white mb-4 text-sm flex items-center gap-2">
              <Clock size={16} className="text-indigo-500" /> Cấu hình Cron Job
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Cron Expression', value: cron.schedule, mono: true },
                { label: 'Mô tả', value: 'Chạy lúc 02:00 sáng hàng ngày', mono: false },
                { label: 'Bull Queue', value: cron.bullQueueActive ? 'ACTIVE' : 'PAUSED', mono: false },
                { label: 'Queue Depth', value: `${cron.queueDepth} job`, mono: false },
                { label: 'Job ID cuối', value: cron.lastJobId ?? '—', mono: true },
                { label: 'Lần chạy cuối', value: cron.lastRunAt ? new Date(cron.lastRunAt).toLocaleString('vi-VN') : '—', mono: false },
              ].map(f => (
                <div key={f.label} className="bg-slate-50 dark:bg-white/[0.02] rounded-xl p-3">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{f.label}</p>
                  <p className={`text-sm mt-0.5 text-slate-700 dark:text-slate-300 ${f.mono ? 'font-mono' : 'font-bold'}`}>{f.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
            <h3 className="font-black text-slate-900 dark:text-white mb-4 text-sm flex items-center gap-2">
              <Database size={16} className="text-indigo-500" /> Pipeline thu thập
            </h3>
            <div className="space-y-3">
              {[
                { step: '①', label: 'Cron trigger / Manual trigger', desc: 'CronService (@Cron) hoặc API POST /collector/kpi-auto/trigger', color: 'slate' },
                { step: '②', label: 'Bull Queue job dispatch', desc: 'Job được enqueue vào Bull Queue "kpi-collector"', color: 'indigo' },
                { step: '③', label: 'Query CSDL nội bộ', desc: 'TC1a: data_assets · TC4a/c: security_events · TC5a/b: soc_logs · TC6a/b: monitoring', color: 'blue' },
                { step: '④', label: 'Convert raw → score (0-10)', desc: 'percentageToScore() · mttrToScore() · cvssCountToScore()', color: 'cyan' },
                { step: '⑤', label: 'Lưu KpiMeasurement', desc: 'Persist vào bảng kpi_measurements với timestamp', color: 'emerald' },
                { step: '⑥', label: 'Trigger preview score', desc: 'Tính lại previewScore + xếp hạng A/B-Tốt/B-Đạt/C', color: 'amber' },
              ].map(s => (
                <div key={s.step} className="flex items-start gap-4">
                  <div className={`w-7 h-7 rounded-lg bg-${s.color}-100 dark:bg-${s.color}-500/15 flex items-center justify-center shrink-0 text-sm font-black text-${s.color}-600 dark:text-${s.color}-400`}>
                    {s.step}
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-900 dark:text-white">{s.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
